import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Scroll-to-top on route change. SPA navigation doesn't reset scroll the
 * way a real document load does — without this hook, a user who scrolled
 * to the bottom of /fortune and then taps /profile lands halfway down
 * the profile screen.
 *
 * Skipped on `POP` (browser back/forward) so the browser's native
 * scroll-restoration takes over for those navigations and the user lands
 * back at the position they left.
 */
export function useScrollToTopOnRouteChange(): void {
  const location = useLocation();
  // POP = back/forward, PUSH = navigate(), REPLACE = navigate({ replace: true })
  const navigationType = useNavigationType();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;

    if (navigationType === 'POP') return;

    // `'instant'` is a valid ScrollBehavior in modern browsers but TS lib
    // hasn't widened the type yet (as of TS 5.7). Cast keeps both happy.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname, navigationType]);
}
